import { LazyLoader } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { type FC, Suspense, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useMatches, useParams } from 'react-router-dom';
import { useQuery } from 'urql';
import { BpPage } from '../../components/BpPage/BpPage';
import {
  DesktopNavigationTabs,
  type DesktopNavigationTabsType,
} from '../../components/NavigationTabs/Desktop/DesktopNavigationTabs';
import { OrganizationConfigContext } from '../../context/OrganizationConfigContext';
import { TeachingUnitContextProvider } from '../../context/TeachingUnitContext';

const CoursePagePageQueryDocument = graphql(`
  query CoursePagePageData($courseUuid: ID!) {
    groups(where: { uuid: $courseUuid, type: course }) {
      uuid
      __typename
      nextcloudConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            enabled
            directLink
            lastSync
          }
        }
      }
      name
      organization {
        uuid
        __typename
      }
    }
  }
`);

export const CoursePage: FC = () => {
  const { t } = useTranslation();
  const { courseUuid } = useParams();
  const { pimAuthClaims } = useAuthClaims();

  const [{ data }] = useQuery({
    query: CoursePagePageQueryDocument,
    variables: { courseUuid: courseUuid! },
    pause: !courseUuid,
  });
  const perms = usePermissionChecker();

  const { cloudLicense, cloudLink } = useContext(OrganizationConfigContext);

  // hack hack hack for overview
  const matches = useMatches();
  const last = [...matches].pop();

  const currentCourse = data?.groups[0];
  const cloudInfo = currentCourse?.nextcloudConnection?.edges.find((e) => e.properties?.enabled);

  const teacherTabs: DesktopNavigationTabsType[] = [
    {
      title: t('common.overview'),
      path: `/courses/${courseUuid}`,
      active: last?.id === 'course-overview',
    },
    {
      title: t('lessons.title'),
      path: `/courses/${courseUuid}/lessons`,
    },

    {
      title: t('assignments.title', { count: 2 }),
      path: `/courses/${courseUuid}/assignments`,
    },
    {
      title: t('workmaterials.title'),
      path: `/courses/${courseUuid}/workmaterials`,
    },
    {
      title: t('appointments.title'),
      path: `/courses/${courseUuid}/appointments`,
    },
    {
      title: t('persons.title', { count: 2 }),
      path: `/courses/${courseUuid}/persons`,
    },
    ...(perms?.isOmniAdmin()
      ? [
          {
            title: 'Admin',
            path: `/courses/${courseUuid}/admin`,
          },
        ]
      : []),
    {
      title: 'Cloud', // TODO: external-link Icon
      path: `https://${cloudInfo?.properties.directLink ?? cloudLink}`,
      target: '_blank',
      disabled: !cloudLicense || cloudLink === '' || !cloudInfo,
    },
  ];

  const studentTabs: DesktopNavigationTabsType[] = [
    {
      title: t('common.overview'),
      path: `/courses/${courseUuid}`,
      active: last?.id === 'course-overview',
    },

    {
      title: t('assignments.title', { count: 2 }),
      path: `/courses/${courseUuid}/assignments`,
    },
    {
      title: t('workmaterials.title'),
      path: `/courses/${courseUuid}/workmaterials`,
    },
    {
      title: t('appointments.title'),
      path: `/courses/${courseUuid}/appointments`,
    },
    {
      title: t('persons.title', { count: 2 }),
      path: `/courses/${courseUuid}/persons`,
    },
    {
      title: 'Gruppenarbeit',
      path: `/courses/${courseUuid}/gruppenarbeit`,
      disabled: true,
    },
    {
      title: 'Cloud', // TODO: external-link Icon
      path: `https://${cloudInfo?.properties.directLink ?? cloudLink}`,
      target: '_blank',
      disabled: !cloudLicense || cloudLink === '' || !cloudInfo,
    },
  ];

  return (
    <TeachingUnitContextProvider>
      <BpPage
        title={currentCourse?.name ?? t('courses.title')}
        isForbidden={
          !currentCourse ||
          !perms?.canViewCourse({
            uuid: courseUuid ?? '',
            organization: currentCourse?.organization,
          })
        }
        organization={pimAuthClaims.getOrganization()}
        hideTitleIfForbidden
      >
        {perms?.canCreateCourse() ? (
          <DesktopNavigationTabs className='mb-6' tabs={teacherTabs} />
        ) : (
          <DesktopNavigationTabs className='mb-6' tabs={studentTabs} />
        )}
        <Suspense fallback={<LazyLoader embedded transparent forceHeight='45vh' />}>{<Outlet />}</Suspense>
      </BpPage>
    </TeachingUnitContextProvider>
  );
};
